'use client';

/**
 * WebBenefitsGridCard — /for-buyers benefits grid.
 *
 * Ports klyde-website/src/pages/ForBuyers.tsx benefits section. 8 tiles
 * in a responsive 2-column grid (single-column on mobile). Each tile
 * carries a lucide-react icon (resolved by name), a title, and body
 * copy. Content via `web_for_buyers_benefits` strategy.
 *
 * Animation is mount-time (delay-staggered), not scroll-triggered —
 * framer-motion's useInView fails inside Canvas2Card's nested
 * overflow-y-auto. Same pattern WebPricingSectionCard uses.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Loader2, XCircle,
  // /for-buyers benefits + landing PersonaCards
  Clock, MessageSquare, BarChart3, Brain, DollarSign, Search, Bot, Globe,
  Building2, Palette, HardHat, Users,
  // /for-suppliers benefits
  Shuffle, ShoppingCart, Zap, Handshake, ClipboardList, MessageCircle, Shield,
  // klyde10 landing sections (search-to-ask / discovery / use cases /
  // three buys / edge)
  MessageCircleQuestion, Sparkles, FileSpreadsheet, MessagesSquare,
  Library, ListTodo, Send, Repeat, PenTool,
  LayoutDashboard, KanbanSquare, ReceiptText, CheckCircle2, ShieldCheck,
  Briefcase,
  type LucideIcon,
} from 'lucide-react';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { api } from '@/lib/api/client';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import type { WebBenefitsGridContent } from '@/lib/api/types';
import { useLanguageStore } from '@/lib/stores/language-store';
import { MediaRenderer } from './media-renderer';


// Icon registry — only the icons used by current prose are loaded.
// Adding a new icon name in YAML = importing it + adding here.
// Keeps the bundle small (no full lucide tree-shake escape hatch).
const ICON_REGISTRY: Record<string, LucideIcon> = {
  // /for-buyers benefits + landing PersonaCards
  Clock, MessageSquare, BarChart3, Brain, DollarSign, Search, Bot, Globe,
  Building2, Palette, HardHat, Users,
  // /for-suppliers benefits
  Shuffle, ShoppingCart, Zap, Handshake, ClipboardList, MessageCircle, Shield,
  // klyde10 landing sections
  MessageCircleQuestion, Sparkles, FileSpreadsheet, MessagesSquare,
  Library, ListTodo, Send, Repeat, PenTool,
  LayoutDashboard, KanbanSquare, ReceiptText, CheckCircle2, ShieldCheck,
  Briefcase,
};


/** Grid column class by tile count — mirrors klyde10's layouts:
 *  8 tiles → 4-col (use cases), 6-7 → 3-col (edge pillars),
 *  3 → 3-col, 2 → 2-col before/after, else the legacy 2-col. */
function gridColsFor(count: number, hasOutcomes: boolean): string {
  if (count >= 8) return 'md:grid-cols-2 lg:grid-cols-4 gap-5';
  if (count >= 6) return 'md:grid-cols-2 lg:grid-cols-3 gap-5';
  if (count === 3 || hasOutcomes) return 'md:grid-cols-3 gap-6';
  if (count === 2) return 'md:grid-cols-2 gap-6';
  return 'md:grid-cols-2';
}


interface WebBenefitsGridCardProps {
  /** Capability action — defaults to for-buyers; landing PersonaCards
   *  passes 'web_landing_personas'. Same renderer, different content. */
  strategyAction?: string;
}

export function WebBenefitsGridCard({
  strategyAction = 'web_for_buyers_benefits',
}: WebBenefitsGridCardProps = {}) {
  const T = useCardTypography();
  const strategy = useCapabilityStore(
    (s) => s.strategyName('content', strategyAction),
  );
  const lang = useLanguageStore((s) => s.lang) ?? undefined;

  const [data, setData] = useState<WebBenefitsGridContent | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!strategy) return;
    let cancelled = false;
    api
      .getStrategyData<WebBenefitsGridContent>(strategy, lang ? { lang } : {})
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setError(res.error || 'Failed to load benefits');
          return;
        }
        setData(res.data ?? null);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message ?? 'Network error');
      });
    return () => { cancelled = true; };
  }, [strategy, lang]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-destructive">
        <XCircle className="h-6 w-6 mb-2" />
        <p className={T.body}>{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Loading…
      </div>
    );
  }

  const hasOutcomes = data.items.some((it) => (it.outcomes ?? []).length > 0);

  return (
    <section className="web-surface relative py-24 px-6 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto">
        {data.eyebrow && (
          <p className={cn(T.metadata, 'text-center uppercase tracking-wider text-primary mb-3')}>
            {data.eyebrow}
          </p>
        )}
        <motion.h2
          className={cn(T.name, 'text-center', data.subheadline ? 'mb-4' : 'mb-16')}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {data.headline}
        </motion.h2>
        {data.subheadline && (
          <p className={cn(T.body, 'text-center italic text-primary max-w-3xl mx-auto mb-14')}>
            {data.subheadline}
          </p>
        )}

        {/* Column count mirrors klyde10's per-section layouts — derived
            from tile count, not per-section config. */}
        <div
          className={cn(
            'grid gap-8 max-w-6xl mx-auto',
            gridColsFor(data.items.length, hasOutcomes),
          )}
        >
          {data.items.map(({ icon, title, body, badge, media_ref, outcomes, cta }, i) => {
            const Icon = ICON_REGISTRY[icon];
            return (
              <motion.div
                key={title}
                className="rounded-xl border border-border bg-card/50 p-8 hover:-translate-y-1 transition-transform duration-200 flex flex-col"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.08, duration: 0.5 }}
              >
                {badge ? (
                  <span className="inline-flex self-start rounded-full bg-primary/10 text-primary text-xs font-semibold tracking-wider px-3 py-1 mb-4">
                    {badge}
                  </span>
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    {Icon ? <Icon size={20} className="text-primary" /> : null}
                  </div>
                )}
                <h3 className={cn(T.sectionLabel, 'mb-2')}>{title}</h3>
                <p className={cn(T.body, 'mb-4 whitespace-pre-line')}>{body}</p>

                {(outcomes ?? []).length > 0 && (
                  <ul className="flex flex-col gap-1.5 mb-4 flex-1">
                    {(outcomes ?? []).map((o) => (
                      <li key={o} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <span className="text-primary mt-0.5">·</span>
                        <span>{o}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {media_ref && (
                  <MediaRenderer
                    mediaRef={media_ref}
                    className="mt-2 w-full rounded-lg overflow-hidden"
                  />
                )}

                {cta && (
                  <a
                    href={cta.href}
                    className="text-sm font-medium text-primary hover:underline mt-auto"
                  >
                    {cta.label}
                  </a>
                )}
              </motion.div>
            );
          })}
        </div>

        {data.footer && (
          <p className={cn(T.body, 'text-center mt-10 max-w-3xl mx-auto whitespace-pre-line')}>
            {data.footer}
          </p>
        )}
      </div>
    </section>
  );
}
